/**
 * Toolbar — zoom dropdown, fit-to-view, set-bbox button, and mode button wiring.
 */

/**
 * Initialise the toolbar with callback handlers.
 *
 * @param {{ onBboxOpen: Function, onZoomChange: Function, onFitToView: Function, onModeChange?: Function }} options
 */
export function initToolbar({ onBboxOpen, onZoomChange, onFitToView, onModeChange }) {
  const btnSetBbox    = document.getElementById("btn-set-bbox");
  const zoomSelect    = document.getElementById("zoom-select");
  const btnFitView    = document.getElementById("btn-fit-view");
  const btnModeSelect = document.getElementById("btn-mode-select");
  const btnModeDraw   = document.getElementById("btn-mode-draw");

  if (btnSetBbox) {
    btnSetBbox.addEventListener("click", () => onBboxOpen());
  }

  if (zoomSelect) {
    // Populate 10%–400% in 10% steps if not already done
    if (zoomSelect.options.length === 0) {
      for (let pct = 10; pct <= 400; pct += 10) {
        const option = document.createElement("option");
        option.value = (pct / 100).toFixed(1);
        option.textContent = `${pct}%`;
        if (pct === 100) option.selected = true;
        zoomSelect.appendChild(option);
      }
    }

    zoomSelect.addEventListener("change", () => {
      const newScale = parseFloat(zoomSelect.value);
      if (!isNaN(newScale)) onZoomChange(newScale);
    });
  }

  if (btnFitView) {
    btnFitView.addEventListener("click", () => onFitToView());
  }

  if (btnModeSelect && onModeChange) {
    btnModeSelect.addEventListener("click", () => onModeChange("select"));
  }

  if (btnModeDraw && onModeChange) {
    btnModeDraw.addEventListener("click", () => onModeChange("draw"));
  }
}

/**
 * Sync the zoom dropdown to reflect the current viewport scale.
 * Called after scroll-wheel zoom so the dropdown stays in sync.
 *
 * @param {number} currentScale - e.g. 1.5 for 150%
 */
export function syncZoomDropdown(currentScale) {
  const zoomSelect = document.getElementById("zoom-select");
  if (!zoomSelect) return;

  // Round to nearest 0.1 to match dropdown option values
  const rounded = (Math.round(currentScale / 0.1) * 0.1).toFixed(1);
  zoomSelect.value = rounded;
}

/**
 * Initialise the color picker dropdown with a palette of swatches.
 *
 * Injects swatch buttons into #color-picker-dropdown, wires toggle on
 * #btn-color-picker, and adds a document-level outside-click listener
 * to close the dropdown.
 *
 * @param {Array<{ name: string, hex: string }>} palette
 * @param {function(string, string): void} onColorChange - Called with (name, hex) when swatch clicked
 */
export function initColorPicker(palette, onColorChange) {
  const btnColorPicker = document.getElementById("btn-color-picker");
  const dropdown = document.getElementById("color-picker-dropdown");
  if (!btnColorPicker || !dropdown) return;

  // Inject swatch buttons
  for (const { name, hex } of palette) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.title = name;
    btn.style.background = hex;
    btn.addEventListener("click", () => {
      onColorChange(name, hex);
      dropdown.classList.remove("open");
    });
    dropdown.appendChild(btn);
  }

  // Toggle dropdown on button click
  btnColorPicker.addEventListener("click", (e) => {
    e.stopPropagation();
    dropdown.classList.toggle("open");
  });

  // Close dropdown on outside click
  document.addEventListener("click", () => {
    dropdown.classList.remove("open");
  });
}

/**
 * Sync the color picker toolbar button swatch to the given hex color.
 *
 * @param {string} hex - Hex color string e.g. "#a8c5a0"
 */
export function syncColorButton(hex) {
  const swatch = document.getElementById("btn-color-swatch");
  if (!swatch) return;
  swatch.style.background = hex;
}

/**
 * Sync the Select/Draw mode buttons to reflect the active mode.
 * Adds .active class to the active button, removes it from the other.
 *
 * @param {"select"|"draw"} activeMode
 */
export function syncModeButtons(activeMode) {
  const btnModeSelect = document.getElementById("btn-mode-select");
  const btnModeDraw   = document.getElementById("btn-mode-draw");
  if (!btnModeSelect || !btnModeDraw) return;

  btnModeSelect.classList.toggle("active", activeMode === "select");
  btnModeDraw.classList.toggle("active", activeMode === "draw");
}

/**
 * Initialise underlay toolbar controls: toggle button and scale slider.
 *
 * @param {{ onToggle: function, onScaleChange: function(number): void }} options
 */
export function initUnderlayControls({ onToggle, onScaleChange }) {
  const toggleBtn   = document.getElementById("btn-underlay-toggle");
  const slider      = document.getElementById("underlay-scale-slider");
  const scaleLabel  = document.getElementById("underlay-scale-label");

  if (toggleBtn) {
    toggleBtn.addEventListener("click", () => onToggle());
  }

  if (slider && scaleLabel) {
    slider.addEventListener("input", () => {
      scaleLabel.textContent = `${slider.value}%`;
      onScaleChange(parseInt(slider.value, 10) / 100);
    });
  }
}

/**
 * Sync the underlay toolbar controls to the current underlay state.
 *
 * @param {boolean} underlayLoaded - true if an underlay image is loaded
 * @param {boolean} visible        - true if underlay is currently visible
 * @param {number}  scale          - current scale factor (e.g. 1.0 = 100%)
 */
export function syncUnderlayControls(underlayLoaded, visible, scale) {
  const toggleBtn  = document.getElementById("btn-underlay-toggle");
  const slider     = document.getElementById("underlay-scale-slider");
  const scaleLabel = document.getElementById("underlay-scale-label");

  if (!toggleBtn || !slider || !scaleLabel) return;

  slider.disabled  = !underlayLoaded;
  toggleBtn.disabled = !underlayLoaded;

  if (underlayLoaded) {
    slider.value        = Math.round(scale * 100);
    scaleLabel.textContent = `${Math.round(scale * 100)}%`;
    toggleBtn.classList.toggle("active", visible);
  } else {
    toggleBtn.classList.remove("active");
    scaleLabel.textContent = `${Math.round(scale * 100)}%`;
  }
}
